import { CommunicationDirection, ConfidentialityLevel, CoverageAction } from '@bariendo/lib/constants';
import { PacIntroCallAnswers } from '@bariendo/lib/patient-access-note';
import { WarmHandoffAnswers } from '@bariendo/lib/patient-access-warm-handoff-note';
import { VoiceCallOutcome } from '@bariendo/lib/voice-call-outcome';

import { isGone } from '@medplum/core';
import {
  Appointment,
  Attachment,
  Claim,
  ClaimResponse,
  Communication,
  DiagnosticReport,
  DocumentReference,
  Encounter,
  OperationOutcome,
  MedicationRequest,
  Practitioner,
  Provenance,
  QuestionnaireResponse,
  Reference,
  Task,
} from '@medplum/fhirtypes';
import { JSONContent } from '@tiptap/react';
import { LocationFragment, OrganizationFragment, ProfileFragment } from '@/services/graphql';
import { TimelineCommunicationResource, TimelineEligibilityFormResource } from '@/pages/PatientTimelinePage/graphql';

export const TimelineItemType = {
  VOICE: 'timeline-voice',
  NOTE: 'timeline-note',
  STANDALONE_NOTE: 'timeline-standalone-note',
  SMS: 'timeline-sms',
  CHAT: 'timeline-chat',
  FAX: 'timeline-fax',
  ENCOUNTER: 'timeline-encounter',
  LAB_ORDER: 'timeline-lab-order',
  LAB_ORDER_RESULTS: 'timeline-lab-order-results',
  PRESCRIPTION_ORDER: 'timeline-prescription-order',
  APPOINTMENT: 'timeline-appointment',
  PAC_INTRO_CALL: 'timeline-ci-intro-call',
  PAC_WARM_HANDOFF: 'timeline-ci-warm-handoff',
  ELIGIBILITY_FORM: 'timeline-eligibility-form',
  PAYMENT_ORDER: 'timeline-payment-order',
  TASK_COMPLETED: 'timeline-task-completed',
  PAYMENT_EVENT: 'timeline-payment-event',
  BENEFIT_EVENT: 'timeline-benefit-event',
  REFUND_EVENT: 'timeline-refund-event',
} as const;

export type TimelineItemType = (typeof TimelineItemType)[keyof typeof TimelineItemType];

/** Mirrors TreatmentPaymentOrder in packages/bots/src/services/treatment-payments.ts. Dollars, not cents. */
export interface TreatmentPaymentOrder {
  checkoutSessionId: string;
  status: string;
  amountTotal: number;
  amountPaid: number;
  balance: number;
  /** ISO 8601. The order's real age; sorts the card on the timeline. */
  createdAt: string;
  items: TreatmentPaymentOrderItem[];
  /** The order-level discount. A line item's own discount is on the item. */
  discount?: TreatmentPaymentOrderDiscount;
  paymentIntents: TreatmentPaymentOrderPaymentIntent[];
}

export interface TreatmentPaymentOrderItem {
  /**
   * Null when the product did not come back hydrated — deliberately not an empty
   * string, so an unresolvable name cannot look like one a caller can resend.
   */
  serviceName: string | null;
  discountName?: string;
  quantity: number;
  amountPrice: number;
  amountDiscount: number;
  amountTotal: number;
}

export interface TreatmentPaymentOrderDiscount {
  id: string;
  name: string;
  amount: number;
  type: string;
}

export interface TreatmentPaymentOrderPaymentIntent {
  id: string;
  processor: string;
  /** The processor's own id (`pi_...` for Stripe) — what a PAC pastes into Stripe. */
  processorRefId: string;
  status: string;
  amount: number;
  createdAt: string;
}

/**
 * One charge against the order — money taken or returned.
 *
 * **Refunds have two representations.** For Stripe charges `type` is always
 * `PAYMENT` and a refund appears as a non-zero `amountRefunded` on the original
 * charge, so keying on `type` alone misses most refunds. A `REFUND` row and a
 * non-zero `amountRefunded` can also describe the *same* money, so summing both
 * double-counts it. `sumRefunded` in `services/payments.ts` is the only place
 * that arithmetic should live.
 */
export interface TreatmentPaymentLedgerEntry {
  chargeId: string;
  /** Matches `TreatmentPaymentOrder.paymentIntents[].id`. */
  paymentIntentId: string;
  type: string;
  amount: number;
  amountRefunded: number;
  processor: string;
  processorRefId: string;
  payerName?: string;
  createdAt: string;
}

export interface TreatmentPaymentLedger {
  checkoutSessionId: string | null;
  /** Newest first. */
  entries: TreatmentPaymentLedgerEntry[];
  /** A page cap was hit, so rows may be missing. A known limit, not a failure. */
  truncated: boolean;
  /** A leg could not be read at all — money that moved is missing from `entries`. */
  partial: boolean;
}

/** Charge types as payment-backend reports them. */
export const LedgerEntryType = {
  PAYMENT: 'PAYMENT',
  REFUND: 'REFUND',
} as const;

/** Mirrors ServiceCatalog in packages/bots/src/services/treatment-payments.ts. */
export interface ServiceCatalogPrice {
  id: string;
  unitAmount: number;
  type: string;
  recurringInterval?: string;
  remark?: string;
}

export interface ServiceCatalogService {
  productId: string;
  /** Send this back as a `serviceNames` entry. */
  name: string;
  nameEs: string;
  prices: ServiceCatalogPrice[];
}

export interface ServiceCatalogDiscount {
  id: string;
  name: string;
  amount: number;
  type: string;
}

/** A Cherry practice a financed payment can be booked against. */
export interface CherryPractice {
  id: string;
  name: string;
}

/**
 * Whether Cherry can actually take a charge for this patient (BAR-2604).
 *
 * Cherry is two phases — the patient applies and is approved for a limit, and
 * only then can a practice charge against it — and we automate only the second.
 * A Cherry send against a patient who never applied therefore fails, and used to
 * fail at charge time as a 502 with Cherry's own reason stripped out.
 *
 * Mirrors `CherryAccountStatus` in `packages/bots`, like the other payment types
 * here.
 *
 * `unknown` never blocks a send: it means the lookup did not answer, which is
 * not evidence the patient is absent. See `docs/cherry.md`.
 */
export type CherryAccountStatus =
  /** Cherry has no account for this phone. A charge cannot succeed. */
  | { state: 'none' }
  /** Applied or invited, but with no approved limit yet. A charge cannot succeed. */
  | { state: 'pending' }
  /** Approved. `amount` is the spending limit in US dollars, not cents. */
  | { state: 'approved'; amount: number; expiresAt?: string }
  /** Cherry knows them but the account cannot be charged — inactive, or an invalid balance. */
  | { state: 'unusable'; reason: string }
  /** No mobile number on file, so there is nothing to look the patient up by. */
  | { state: 'no-phone' }
  /** The lookup did not answer. Never blocks. */
  | { state: 'unknown'; reason: string };

/**
 * The result of initiating a treatment payment.
 *
 * A union rather than an optional `paymentLink`: Stripe mints a URL for the PAC
 * to send, while Cherry texts the patient itself and hands back nothing. The
 * drawer branches on `processor` so a Cherry send never offers a Copy-link
 * button that would copy nothing.
 */
export type SentTreatmentPayment =
  | {
      processor: 'STRIPE';
      checkoutSessionId: string;
      amount: number;
      paymentLink: string;
    }
  | {
      processor: 'CHERRY';
      checkoutSessionId: string;
      amount: number;
      /** Last four only — enough to confirm the destination, not to read it back. */
      requestedToLast4: string;
    };

/**
 * The outcome of Orbit texting a Stripe payment link itself.
 *
 * Distinct from `SentTreatmentPayment` because it describes a different send by
 * a different system: `sentAt` is a real time Orbit observed, whereas the email
 * payment-backend sends alongside it has no recoverable timestamp at all.
 */
export interface TextedPaymentLink {
  /** ISO 8601, from the `Communication` the send created. */
  sentAt: string;
  /** Last four only — enough to confirm the destination, not to read it back. */
  toLast4: string;
}

export interface VirtualReceptionistData {
  caller_phone_number: string;
  lead_id: string;
  activity_at: string;
  date_created: string;
  caller_name: string;
  requested_representative_name: string;
  call_type: string;
  question: string;
  message: string;
  full_messsage_link: string;
  answerconnect_message_id: string;
}

export interface VirtualReceptionistProvidersTried {
  url?: string;
  valueReference?: {
    reference?: string;
    display?: string;
  };
}

export interface TwilioCallError {
  errorCode: string;
  errorMessage: string;
  timestamp: string;
  recordingStatus?: string;
}

export interface BaseTimelineItem {
  reference: Reference<Communication | Encounter | DiagnosticReport>;
  resource: TimelineCommunicationResource;
  sender?: ProfileFragment | OrganizationFragment;
  recipient?: ProfileFragment | OrganizationFragment;
  status?: Communication['status'];
  timestamp: Date;
  content?: string;
  confidentiality?: ConfidentialityLevel;
  hiddenPayload: boolean;
  priorityStatus?: 'normal' | 'urgent' | 'overdue';
  isAssignable?: boolean;
}

export interface TimelineNote extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.NOTE;
  linkedTo: Reference<Communication | Encounter>;
  richTextContent?: JSONContent;
  parentConfidentiality?: ConfidentialityLevel;
  isEdited?: boolean;
}

export interface TimelineStandaloneNoteCommunication extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.STANDALONE_NOTE;
  richTextContent?: object;
  isEdited?: boolean;
}

/**
 * Patient Access intro-call card — and, on `answers.visitType === 'pn-consultation'`,
 * the PN consultation card that shares its note. `isDraft` mirrors
 * Communication.status `in-progress`; a draft is still being worked and stays editable.
 */
export interface TimelineCiIntroCallCommunication extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.PAC_INTRO_CALL;
  answers: PacIntroCallAnswers;
  isDraft: boolean;
}

/**
 * Patient Access warm hand-off card — the call after the NP consult. `isDraft`
 * mirrors Communication.status `in-progress`; a draft is still being worked by
 * the rep and stays editable.
 */
export interface TimelineWarmHandoffCommunication extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.PAC_WARM_HANDOFF;
  answers: WarmHandoffAnswers;
  isDraft: boolean;
}

export interface TimelineVoiceCommunication extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.VOICE;
  direction: CommunicationDirection | undefined;
  duration?: number;
  recordingAttachments?: Attachment[];
  patientNumber: string;
  /** The Everself line this call came in on / went out from. Same resource,
   * opposite extension from `patientNumber` — no extra read. */
  orbitNumber: string;
  virtualReceptionistData?: VirtualReceptionistData;
  virtualReceptionistProvidersTriedHistory?: VirtualReceptionistProvidersTried[];
  twilioErrors?: TwilioCallError[];
  callOutcome?: VoiceCallOutcome;
  recordingUnavailableMessage?: string;
}

export interface SmsError {
  errorCode?: string;
  errorMessage?: string;
  timestamp?: string;
}

export interface TimelineSmsCommunication extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.SMS;
  direction: CommunicationDirection | undefined;
  mediaAttachments?: Attachment[];
  pdfAttachments?: Attachment[];
  patientNumber: string;
  /** The Everself line this text was received on / sent from. A thread can
   * span more than one when the from-number rules change mid-conversation. */
  orbitNumber: string;
  errorCode?: string;
  errorMessage?: string;
  errors?: SmsError[];
}

export interface TimelineChatCommunication extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.CHAT;
  direction: CommunicationDirection | undefined;
  mediaAttachments?: Attachment[];
  pdfAttachments?: Attachment[];
  thread?: Reference<Communication>;
  richTextContent?: JSONContent;
  errorCode?: string;
  errorMessage?: string;
  errors?: SmsError[];
}

export interface TimelineFaxCommunication extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.FAX;
  direction: CommunicationDirection | undefined;
  mediaAttachments?: Attachment[];
  pdfAttachments?: Attachment[];
  /** Remote fax line or organization name for search/display */
  faxCounterparty: string;
}

export interface TimelineEncounter extends BaseTimelineItem {
  reference: Reference<Encounter>;
  type: typeof TimelineItemType.ENCOUNTER;
  encounter: Encounter & { tasks?: Task[]; history?: Provenance[] };
}

export interface TimelineLabOrder extends BaseTimelineItem {
  reference: Reference<Communication>;
  type: typeof TimelineItemType.LAB_ORDER;
  serviceRequestReferences: Reference[];
  questionnaireResponseReference?: Reference;
}

export interface TimelineLabOrderResults extends BaseTimelineItem {
  reference: Reference<DiagnosticReport>;
  type: typeof TimelineItemType.LAB_ORDER_RESULTS;
  diagnosticReport: DiagnosticReport;
  serviceRequestReference?: Reference;
}

export interface TimelinePrescriptionOrder {
  reference: Reference<MedicationRequest>;
  type: typeof TimelineItemType.PRESCRIPTION_ORDER;
  medicationRequest: MedicationRequest;
  timestamp: Date;
  requester?: ProfileFragment;
  medicationDisplay?: string;
  dosageInstruction?: string;
  quantity?: string;
}

export interface TimelineAppointment {
  reference: Reference;
  type: typeof TimelineItemType.APPOINTMENT;
  appointment: Appointment;
  timestamp: Date;
  contactType?: 'Video Call' | 'Phone Call' | 'In-Person';
  location?: LocationFragment;
  virtualServiceUrl?: string;
  /** Internal/admin URL for virtual services (e.g., Zoom startUrl) */
  virtualServiceAdminUrl?: string;
  virtualServicePhone?: string;
}

/**
 * Eligibility form completed on everself.com (BAR-2495). Not a Communication —
 * a QuestionnaireResponse against Questionnaire/eligibility-v21, merged into the
 * spine by its own query.
 */
export interface TimelineEligibilityForm {
  reference: Reference<QuestionnaireResponse>;
  type: typeof TimelineItemType.ELIGIBILITY_FORM;
  questionnaireResponse: TimelineEligibilityFormResource;
  timestamp: Date;
  /** Eligibility-session identifier, for deep-linking and dedup. */
  sessionId?: string;
}

/**
 * A payment order card (surface 01).
 *
 * Deliberately does NOT extend BaseTimelineItem: payments have no FHIR resource
 * in this system, and BaseTimelineItem requires both a FHIR reference and a
 * Communication resource. Follows TimelineAppointment and TimelineEligibilityForm,
 * which sidestep it the same way.
 *
 * Identity is `id` — the payment order's own id — not `reference`. There is no
 * resource type to build a reference from, and inventing `CheckoutSession/<id>`
 * would be a fake FHIR reference.
 */
export interface TimelineTaskCompleted {
  reference: Reference;
  type: typeof TimelineItemType.TASK_COMPLETED;
  id: string;
  title: string;
  /** From the task-completed-by extension; absent on tasks completed before the stamp shipped. */
  completedBy?: Reference<Practitioner>;
  /** Completion time (Task.lastModified, stamped by the guarded patch protocol). */
  timestamp: Date;
}

/**
 * An employer benefit event (BAR-2593) — a pre-authorization Claim (Submitted) or a
 * ClaimResponse (Approved / Denied). One card per resource; the record is append-only.
 */
export interface TimelineBenefitEvent {
  reference: Reference<Claim | ClaimResponse>;
  type: typeof TimelineItemType.BENEFIT_EVENT;
  id: string;
  /** The coverage step recorded (BAR-3078); BAR-2593 records read as their step. Unset on Prior auth ready (BAR-3089). */
  action?: CoverageAction;
  /** The card's title, the spec's words for the step. */
  title: string;
  payorName: string;
  /** Operator-entered action date, YYYY-MM-DD — what commission reads, and what positions the card. */
  actionDate: string;
  /** Files recorded with the step. */
  documents: Reference<DocumentReference>[];
  note?: string;
  recordedBy?: Reference;
  recordedByName?: string;
  recordedAt?: Date;
  timestamp: Date;
}

export interface TimelinePaymentOrder {
  reference: Reference;
  type: typeof TimelineItemType.PAYMENT_ORDER;
  id: string;
  order: TreatmentPaymentOrder;
  timestamp: Date;
  /**
   * Total returned to the patient. Reconciled by `sumRefunded` — refunds have
   * two representations that can alias each other, so this is not a plain sum.
   */
  amountRefunded: number;
  /** The ledger hit a page cap; some charges may be missing. A known limit. */
  ledgerTruncated: boolean;
  /** A payment leg could not be read at all — money that moved is missing. */
  ledgerPartial: boolean;
  /**
   * Payment-intent id -> when its charge settled, from the ledger. Dates the
   * `Paid · Jul 18` label on a method row. Keyed rather than folded into the
   * intent so the order projection stays exactly what the backend sent.
   */
  legSettledAt: Record<string, string>;
  /**
   * Display name of whoever created the order, when a `Provenance` says so.
   * Absent for pre-BAR-2614 and legacy Close-path orders.
   */
  createdBy?: string;
}

/**
 * An append-only money event: card 02 (`$X paid via …`) and card 03
 * (`… paid in full`). Immutable, which is what makes the timeline a usable
 * financial audit trail.
 *
 * There is no refund variant. A Stripe refund is a cumulative `amountRefunded`
 * on the original charge rather than an event, so it has no date of its own to
 * put on a card; the refunded total lives on card 01 instead. Refund requests
 * have dates, and their own cards: {@link TimelineRefundEvent}.
 */
export interface TimelinePaymentEvent {
  reference: Reference;
  type: typeof TimelineItemType.PAYMENT_EVENT;
  id: string;
  kind: 'paid' | 'paid-in-full';
  amount: number;
  processor?: string;
  processorRefId?: string;
  payerName?: string;
  serviceName?: string;
  /**
   * Display name of the teammate who keyed this settlement by hand, from the
   * charge's `Provenance`. Absent for a webhook-written charge, which is what
   * distinguishes the two on the card — never inferred from the processor.
   */
  recordedBy?: string;
  timestamp: Date;
}

/**
 * One step of a refund request (BAR-3052): requested (or a $0 cancellation),
 * approved, issued. Append-only like the payment cards, and built the same way,
 * from the payments backend rather than a FHIR resource.
 */
export interface TimelineRefundEvent {
  reference: Reference;
  type: typeof TimelineItemType.REFUND_EVENT;
  id: string;
  kind: 'requested' | 'cancelled' | 'approved' | 'issued';
  title: string;
  /** The reason and note, or Finance's approval note. */
  note?: string;
  /** `Practitioner/<id>`; absent when a refund's arrival linked the request. */
  recordedBy?: string;
  timestamp: Date;
}

export type TimelinePatientCommunication =
  | TimelineChatCommunication
  | TimelineSmsCommunication
  | TimelineVoiceCommunication
  | TimelineFaxCommunication
  | TimelineStandaloneNoteCommunication
  | TimelineCiIntroCallCommunication
  | TimelineWarmHandoffCommunication
  | TimelineLabOrder
  | TimelineLabOrderResults;

export type TimelineItem =
  | TimelinePatientCommunication
  | TimelineNote
  | TimelineEncounter
  | TimelineLabOrder
  | TimelineAppointment
  | TimelineLabOrderResults
  | TimelinePrescriptionOrder
  | TimelineEligibilityForm
  | TimelinePaymentOrder
  | TimelineTaskCompleted
  | TimelinePaymentEvent
  | TimelineBenefitEvent
  | TimelineRefundEvent;

export function isTextBasedCommunication(
  item: TimelineItem
): item is TimelineChatCommunication | TimelineSmsCommunication {
  return isSmsCommunication(item) || isChatCommunication(item);
}

export function isSmsCommunication(item: TimelineItem): item is TimelineSmsCommunication {
  return item.type === TimelineItemType.SMS;
}

export function isFaxTimelineCommunication(item: TimelineItem): item is TimelineFaxCommunication {
  return item.type === TimelineItemType.FAX;
}

export function isChatCommunication(item: TimelineItem): item is TimelineChatCommunication {
  return item.type === TimelineItemType.CHAT;
}

export function isVoiceCommunication(item: TimelineItem): item is TimelineVoiceCommunication {
  return item.type === TimelineItemType.VOICE;
}

export function isStandaloneNote(item: TimelineItem): item is TimelineStandaloneNoteCommunication {
  return item.type === TimelineItemType.STANDALONE_NOTE;
}

export function isCiIntroCall(item: TimelineItem): item is TimelineCiIntroCallCommunication {
  return item.type === TimelineItemType.PAC_INTRO_CALL;
}

export function isWarmHandoff(item: TimelineItem): item is TimelineWarmHandoffCommunication {
  return item.type === TimelineItemType.PAC_WARM_HANDOFF;
}

export function isTimelineNote(item: TimelineItem): item is TimelineNote {
  return item.type === TimelineItemType.NOTE;
}

export function isEncounterItem(item: TimelineItem): item is TimelineEncounter {
  return item.type === TimelineItemType.ENCOUNTER;
}

export function isLabOrderItem(item: TimelineItem): item is TimelineLabOrder {
  return item.type === TimelineItemType.LAB_ORDER;
}

export function isLabOrderResultsItem(item: TimelineItem): item is TimelineLabOrderResults {
  return item.type === TimelineItemType.LAB_ORDER_RESULTS;
}

export function isPrescriptionOrderItem(item: TimelineItem): item is TimelinePrescriptionOrder {
  return item.type === TimelineItemType.PRESCRIPTION_ORDER;
}

export function isAppointmentItem(item: TimelineItem): item is TimelineAppointment {
  return item.type === TimelineItemType.APPOINTMENT;
}

export function isEligibilityFormItem(item: TimelineItem): item is TimelineEligibilityForm {
  return item.type === TimelineItemType.ELIGIBILITY_FORM;
}

export type BaseTimelineGroup = {
  id: string;
  timestamp: Date;
  notes?: TimelineNote[];
};

export type VoiceCallTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.VOICE;
  items: TimelineVoiceCommunication[];
};

export type StandaloneNoteGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.STANDALONE_NOTE;
  items: TimelineStandaloneNoteCommunication[];
};

export type PacIntroCallTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.PAC_INTRO_CALL;
  items: TimelineCiIntroCallCommunication[];
};

export type WarmHandoffTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.PAC_WARM_HANDOFF;
  items: TimelineWarmHandoffCommunication[];
};

export type SmsTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.SMS;
  items: TimelineSmsCommunication[];
};

export type FaxTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.FAX;
  items: TimelineFaxCommunication[];
};

export type ChatTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.CHAT;
  items: TimelineChatCommunication[];
};

export type EncounterTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.ENCOUNTER;
  items: TimelineEncounter[];
};

export type LabOrderTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.LAB_ORDER;
  items: TimelineLabOrder[];
};

export type LabOrderResultsTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.LAB_ORDER_RESULTS;
  items: TimelineLabOrderResults[];
};

export type PrescriptionOrderTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.PRESCRIPTION_ORDER;
  items: TimelinePrescriptionOrder[];
};

export type AppointmentTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.APPOINTMENT;
  items: TimelineAppointment[];
};

export type EligibilityFormTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.ELIGIBILITY_FORM;
  items: TimelineEligibilityForm[];
};

export type PaymentOrderTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.PAYMENT_ORDER;
  items: TimelinePaymentOrder[];
};

export type TaskCompletedTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.TASK_COMPLETED;
  items: TimelineTaskCompleted[];
};

export type PaymentEventTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.PAYMENT_EVENT;
  items: TimelinePaymentEvent[];
};

export type BenefitEventTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.BENEFIT_EVENT;
  items: TimelineBenefitEvent[];
};

export type RefundEventTimelineGroup = BaseTimelineGroup & {
  type: typeof TimelineItemType.REFUND_EVENT;
  items: TimelineRefundEvent[];
};

export type TimelineGroup =
  | VoiceCallTimelineGroup
  | StandaloneNoteGroup
  | PacIntroCallTimelineGroup
  | WarmHandoffTimelineGroup
  | ChatTimelineGroup
  | SmsTimelineGroup
  | FaxTimelineGroup
  | EncounterTimelineGroup
  | LabOrderTimelineGroup
  | LabOrderResultsTimelineGroup
  | PrescriptionOrderTimelineGroup
  | AppointmentTimelineGroup
  | EligibilityFormTimelineGroup
  | PaymentOrderTimelineGroup
  | TaskCompletedTimelineGroup
  | PaymentEventTimelineGroup
  | BenefitEventTimelineGroup
  | RefundEventTimelineGroup;

export function isVoiceGroup(group: TimelineGroup): group is VoiceCallTimelineGroup {
  return group.type === TimelineItemType.VOICE;
}

export function isStandaloneNoteGroup(group: TimelineGroup): group is StandaloneNoteGroup {
  return group.type === TimelineItemType.STANDALONE_NOTE;
}

export function isCiIntroCallGroup(group: TimelineGroup): group is PacIntroCallTimelineGroup {
  return group.type === TimelineItemType.PAC_INTRO_CALL;
}

export function isWarmHandoffGroup(group: TimelineGroup): group is WarmHandoffTimelineGroup {
  return group.type === TimelineItemType.PAC_WARM_HANDOFF;
}

export function isChatGroup(group: TimelineGroup): group is ChatTimelineGroup {
  return group.type === TimelineItemType.CHAT;
}

export function isSmsGroup(group: TimelineGroup): group is SmsTimelineGroup {
  return group.type === TimelineItemType.SMS;
}

export function isFaxGroup(group: TimelineGroup): group is FaxTimelineGroup {
  return group.type === TimelineItemType.FAX;
}

export function isEncounterGroup(group: TimelineGroup): group is EncounterTimelineGroup {
  return group.type === TimelineItemType.ENCOUNTER;
}

export function isLabOrderGroup(group: TimelineGroup): group is LabOrderTimelineGroup {
  return group.type === TimelineItemType.LAB_ORDER;
}

export function isLabOrderResultsGroup(group: TimelineGroup): group is LabOrderResultsTimelineGroup {
  return group.type === TimelineItemType.LAB_ORDER_RESULTS;
}

export function isPrescriptionOrderGroup(group: TimelineGroup): group is PrescriptionOrderTimelineGroup {
  return group.type === TimelineItemType.PRESCRIPTION_ORDER;
}

export function isAppointmentGroup(group: TimelineGroup): group is AppointmentTimelineGroup {
  return group.type === TimelineItemType.APPOINTMENT;
}

export function isEligibilityFormGroup(group: TimelineGroup): group is EligibilityFormTimelineGroup {
  return group.type === TimelineItemType.ELIGIBILITY_FORM;
}

export function isPaymentOrderGroup(group: TimelineGroup): group is PaymentOrderTimelineGroup {
  return group.type === TimelineItemType.PAYMENT_ORDER;
}

export function isTaskCompletedGroup(group: TimelineGroup): group is TaskCompletedTimelineGroup {
  return group.type === TimelineItemType.TASK_COMPLETED;
}

export function isPaymentEventGroup(group: TimelineGroup): group is PaymentEventTimelineGroup {
  return group.type === TimelineItemType.PAYMENT_EVENT;
}

export function isBenefitEventGroup(group: TimelineGroup): group is BenefitEventTimelineGroup {
  return group.type === TimelineItemType.BENEFIT_EVENT;
}

export function isRefundEventGroup(group: TimelineGroup): group is RefundEventTimelineGroup {
  return group.type === TimelineItemType.REFUND_EVENT;
}

export const ContactPointType = {
  PHONE: 'phone',
  EMAIL: 'email',
} as const;

export type ContactPointType = (typeof ContactPointType)[keyof typeof ContactPointType];

export const ContactPointRank = {
  PRIMARY: 1,
  SECONDARY: 2,
} as const;

export type ContactPointRank = (typeof ContactPointRank)[keyof typeof ContactPointRank];

export const MessageBoxType = {
  SMS: 'sms',
  CHAT: 'chat',
  STANDALONE_NOTE: 'standalone_note',
  ENCOUNTER: 'encounter',
  LAB_ORDER: 'lab_order',
  TASK: 'task',
  COVERAGE_ACTIVITY: 'coverage_activity',
  /** Refund or cancellation request, inline (BAR-3069). */
  REFUND_REQUEST: 'refund_request',
} as const;

export type MessageBoxType = (typeof MessageBoxType)[keyof typeof MessageBoxType];

// Narrowing helpers to avoid `any` when checking errors ---
function hasResourceType(obj: unknown): obj is typeof obj & { resourceType: string } {
  return typeof obj === 'object' && obj !== null && 'resourceType' in obj;
}

function isOperationOutcome(err: unknown): err is OperationOutcome {
  return hasResourceType(err) && err.resourceType === 'OperationOutcome';
}
// ---

export function isNotFoundError(err: OperationOutcome | Error): boolean {
  if (isOperationOutcome(err)) {
    const text = (err.issue ?? [])
      .map((i) => i.diagnostics || i.details?.text || '')
      .join(' ')
      .toLowerCase();
    return /not\s*found/.test(text);
  }
  return /not\s*found|404/.test((err.message || '').toLowerCase());
}

export function isGoneError(err: OperationOutcome | Error): boolean {
  if (isOperationOutcome(err)) {
    return isGone(err);
  }
  return /gone|410/i.test((err.message || '').toLowerCase());
}
